import React, { useState } from 'react';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Button as MuiButton, Grid, CircularProgress } from '@mui/material';
import styles from './DeviceForm.module.scss';
import deviceStyles from './GamemasterUserDevices.module.scss';
import { useQueryClient } from 'react-query';

interface EditDeviceSettingsProps {
    device: any;
    devices: any[];
    workplaceId: number;
    handleBackClick: () => void;
    onDeviceUpdated?: () => void;
}

const EditDeviceSettings: React.FC<EditDeviceSettingsProps> = ({
    device,
    devices,
    workplaceId,
    handleBackClick,
    onDeviceUpdated,
}) => {
    const [editName, setEditName] = useState(device?.name || '');
    const [editIdentifier, setEditIdentifier] = useState(device?.identifier || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    if (!device) {
        return <div className={styles.loading}><CircularProgress size={20} /></div>;
    }

    // Check for duplicate name (excluding current device)
    const isDuplicate = devices.some(
        (d: any) => d.id !== device.id && d.name.toLowerCase() === editName.toLowerCase()
    );

    const handleUpdateDevice = async () => {
        setError(null);
        setIsLoading(true);

        if (!editName.trim() || !editIdentifier.trim()) {
            setError('Please enter both device name and type.');
            setIsLoading(false);
            return;
        }
        if (isDuplicate) {
            setError('This device name already exists.');
            setIsLoading(false);
            return;
        }

        try {
            // Note: Update API doesn't exist yet, so we'll show a message
            // When API is available, uncomment and use it:
            // await updateDevice(device.id, { name: editName, identifier: editIdentifier });
            // await queryClient.invalidateQueries(['userDevices', workplaceId]);
            setError('Update device functionality will be available soon.');
            // For now, just close the dialog
            // handleBackClick();
        } catch (error: any) {
            console.error('Error updating device:', error);
            setError(error?.message || 'Failed to update device. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = editName.trim() && editIdentifier.trim() && !isDuplicate;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleUpdateDevice();
    };

    return (
        <div className={deviceStyles.createDeviceSetting}>
            <div className={deviceStyles.formContainer}>
                <Grid container direction="column" spacing={2} pl={2}>
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid>
                            <MuiButton className={deviceStyles.backButton} onClick={handleBackClick}>
                                Back
                            </MuiButton>
                        </Grid>
                        <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                            <Grid>
                                <MuiButton
                                    className={deviceStyles.saveButton}
                                    onClick={handleSubmit}
                                    disabled={isLoading || !isFormValid}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </MuiButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                        <h2 className={deviceStyles.formTitle}>Edit Device</h2>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={deviceStyles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <label className={styles.label}>Device Name</label>
                            <Input
                                type="text"
                                required
                                placeholder="Device Name"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className={styles.input}
                            />
                            {isDuplicate && (
                                <span className={styles.fieldError}>This device name already exists.</span>
                            )}
                        </div>

                        <div className={styles.formField}>
                            <label className={styles.label}>Device Type</label>
                            <Select value={editIdentifier} onValueChange={setEditIdentifier}>
                                <SelectTrigger className={styles.select}>
                                    <SelectValue placeholder="Select Device Type" />
                                </SelectTrigger>
                                <SelectContent className={styles.selectContent}>
                                    <SelectItem className={styles.selectItem} value="ipad">iPad</SelectItem>
                                    <SelectItem className={styles.selectItem} value="mobile">Mobile</SelectItem>
                                    <SelectItem className={styles.selectItem} value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditDeviceSettings;

